/**
 * OnSiteComms — photo watermark ("carimbo").
 * Burns date + time + job label into the bottom of the photo before upload,
 * turning every sent photo into proof-of-work (like job-site cameras).
 */

/**
 * @param {string} dataUrl  JPEG/PNG data URL of the photo
 * @param {object} opts     { label?: string, date?: Date, lang?: "en"|"pt" }
 * @returns {Promise<string>} data URL with the stamp burned in
 */
export async function applyPhotoWatermark(dataUrl, { label = "", date = new Date(), lang = "en" } = {}) {
  const img = await loadImage(dataUrl);
  const canvas = document.createElement("canvas");
  canvas.width = img.naturalWidth || img.width;
  canvas.height = img.naturalHeight || img.height;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0);

  const W = canvas.width;
  const H = canvas.height;

  // Scale everything relative to the image size (min side) so the stamp is
  // readable on both thumbnails and full-size photos.
  const base = Math.max(Math.min(W, H) * 0.032, 14);
  const pad = base * 0.6;
  const line1 = fmtStampDate(date, lang);
  const line2 = (label || "").trim();

  ctx.textBaseline = "alphabetic";
  const font1 = `600 ${Math.round(base)}px -apple-system, 'Segoe UI', Roboto, sans-serif`;
  const font2 = `500 ${Math.round(base * 0.85)}px -apple-system, 'Segoe UI', Roboto, sans-serif`;

  ctx.font = font1;
  const w1 = ctx.measureText(line1).width;
  ctx.font = font2;
  const w2 = line2 ? ctx.measureText(line2).width : 0;
  const textW = Math.min(Math.max(w1, w2), W - pad * 2);
  const lineH1 = base * 1.25;
  const lineH2 = line2 ? base * 1.1 : 0;
  const boxH = pad * 0.9 + lineH1 + lineH2 + pad * 0.55;
  const boxW = textW + pad * 1.6;
  const x = pad * 0.7;
  const y = H - boxH - pad * 0.7;

  // Rounded semi-transparent plate (keeps the stamp readable on any photo)
  ctx.fillStyle = "rgba(0,0,0,0.55)";
  roundRect(ctx, x, y, boxW, boxH, base * 0.45);
  ctx.fill();

  ctx.fillStyle = "#ffffff";
  ctx.font = font1;
  ctx.fillText(line1, x + pad * 0.8, y + pad * 0.75 + base * 0.85, W - pad * 3);
  if (line2) {
    ctx.fillStyle = "rgba(255,255,255,0.92)";
    ctx.font = font2;
    ctx.fillText(line2, x + pad * 0.8, y + pad * 0.75 + lineH1 + base * 0.72, W - pad * 3);
  }

  return canvas.toDataURL("image/jpeg", 0.9);
}

function fmtStampDate(date, lang) {
  const locale = lang === "pt" ? "pt-BR" : "en-US";
  const d = date.toLocaleDateString(locale, { day: "2-digit", month: "2-digit", year: "numeric" });
  const t = date.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
  return `${d} ${t}`;
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}
